import { useEffect } from "react";

export const useGameEventListeners = (
  socket,
  {
    setStocks,
    setIndexes,
    setActiveEvents,
    setNewEventIds,
    setVisualEffects,
    setStockOwnershipCounts,
    setStockOwnershipByPlayer,
    setIndexOwnershipByPlayer,
    setGameLog,
    processGameLogUpdate,
    showMessage
  }
) => {
  useEffect(() => {
    const currentSocket = socket.current;
    if (!currentSocket) return;

    const processActiveEvents = (activeEvents, prevEvents) => {
      if (!Array.isArray(activeEvents)) {
        return { newEvents: [], shouldUpdate: true };
      }

      const prevIds = new Set(prevEvents.map(e => e.id || e.name));
      const newEvents = activeEvents.filter(e => {
        const eventId = e.id || e.name;
        return !prevIds.has(eventId);
      });
      
      return { newEvents, shouldUpdate: true };
    };

    const handleStocksUpdate = ({ stocks, indexes, activeEvents, visualEffects, gameLog, stockOwnershipCounts, stockOwnershipByPlayer, indexOwnershipByPlayer }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] stocks_update received');
      }
      setStocks(Array.isArray(stocks) ? stocks : []);
      setIndexes(Array.isArray(indexes) ? indexes : []);
      
      if (Array.isArray(activeEvents)) {
        setActiveEvents(prev => {
          const { newEvents } = processActiveEvents(activeEvents, prev);
          
          if (newEvents.length > 0) {
            setNewEventIds(prevIds => {
              const updated = new Set(prevIds);
              newEvents.forEach(e => {
                const eventId = e.id || e.name;
                if (eventId) updated.add(eventId);
              });
              return updated;
            });
          }
          
          return activeEvents;
        });
      } else {
        setActiveEvents([]);
      }
      
      setVisualEffects(Array.isArray(visualEffects) ? visualEffects : []);
      setStockOwnershipCounts(stockOwnershipCounts || {});
      setStockOwnershipByPlayer(stockOwnershipByPlayer || {});
      // Only update indexOwnershipByPlayer if it's provided (don't clear if undefined)
      if (indexOwnershipByPlayer !== undefined) {
        setIndexOwnershipByPlayer(indexOwnershipByPlayer);
      }
      if (Array.isArray(gameLog)) {
        processGameLogUpdate(gameLog);
        setGameLog(gameLog);
      }
    };

    const handleEventPlayed = ({ event, indexes }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] event_played received', { event: event?.name || event?.id });
      }
      setActiveEvents((prev) => {
        const updated = [...prev, event];
        if (event && event.id) {
          setNewEventIds(prev => new Set([...prev, event.id]));
        } else if (event && event.name) {
          setNewEventIds(prev => new Set([...prev, event.name]));
        }
        return updated;
      });
      setIndexes(Array.isArray(indexes) ? indexes : []);
    };

    const handleEventTriggered = ({ event, roll, results, indexes }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] event_triggered received', { event: event?.name || event?.id });
      }
      setIndexes(Array.isArray(indexes) ? indexes : []);
    };

    const handleRoundMessage = ({ round, message, indexes, activeEvents, recentLog }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] round_message received', { round });
      }
      setIndexes(Array.isArray(indexes) ? indexes : []);
      setActiveEvents(Array.isArray(activeEvents) ? activeEvents : []);
      if (Array.isArray(recentLog)) {
        processGameLogUpdate(recentLog);
        setGameLog(recentLog);
      }
      showMessage(message);
    };

    const handleGameLogUpdate = (log) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] game_log_update received', { logLength: Array.isArray(log) ? log.length : 0 });
      }
      if (Array.isArray(log)) {
        processGameLogUpdate(log);
        setGameLog(log);
      }
    };

    currentSocket.on("stocks_update", handleStocksUpdate);
    currentSocket.on("event_played", handleEventPlayed);
    currentSocket.on("event_triggered", handleEventTriggered);
    currentSocket.on("round_message", handleRoundMessage);
    currentSocket.on("game_log_update", handleGameLogUpdate);

    return () => {
      currentSocket.off("stocks_update", handleStocksUpdate);
      currentSocket.off("event_played", handleEventPlayed);
      currentSocket.off("event_triggered", handleEventTriggered);
      currentSocket.off("round_message", handleRoundMessage);
      currentSocket.off("game_log_update", handleGameLogUpdate);
    };
  }, [socket, setStocks, setIndexes, setActiveEvents, setNewEventIds, setVisualEffects, setStockOwnershipCounts, setStockOwnershipByPlayer, setIndexOwnershipByPlayer, setGameLog, processGameLogUpdate, showMessage]);
};

